import { Injectable } from '@angular/core';
import { ErrorMessageResponse } from '@app/core/models/data-response';
import { DataTaskService } from '@app/core/services/data-task.service';
import { FileItem } from '@app/core/services/file.service';
import { Attachment } from '@microsoft/microsoft-graph-types';

@Injectable({
    providedIn: 'root',
})
export class DocArcService {
    /**
     * Saves the given item as email item in docarc.
     *
     * @datatask 4014
     * @datataskName tt_docarc_email_item_create
     * @param params
     * @returns promise containing validation.
     */
    public async saveEmailItem(params: DocArcSaveEmailItemParams): Promise<ErrorMessageResponse> {
        return (await this.datatask.Post(4014, params))[0];
    }

    constructor(private datatask: DataTaskService) {}

    public async add(docarc: DocArcItem): Promise<{ docarc_keyno: `${number}`; docfilename: string }> {
        if (!docarc.filename?.trim()) throw Error('Filename is missing, required for adding file to docarc');

        docarc.contentType = docarc.contentType || '';
        docarc.latitude = docarc.latitude || '0';
        docarc.longitude = docarc.longitude || '0';
        docarc.altitude = docarc.altitude || '0';
        docarc.heading = docarc.heading || '0';
        docarc.createdTimestamp = docarc.createdTimestamp || '';
        docarc.originalTimestamp = docarc.originalTimestamp || '';
        docarc.size = docarc.size || '';
        docarc.argType = docarc.argType || '';
        docarc.argValue = docarc.argValue || '';
        docarc.checksum_md5 = docarc.checksum_md5 || '';

        // var internaldocarc = {
        //     method: 310,
        //     parameters: docarc
        // };

        // var deferred = $q.defer();

        // $ihttp.post(internaldocarc, { context: context });
        return (await this.datatask.Post(310, docarc))[0];
    }

    /**
     * Retrieves list of document categories.
     *
     * @datatask 3727
     * @datataskName sp_ws_p2_doccategory_list
     * @returns a promise containing a list of document categories.
     */
    public getDocumentCategories(): Promise<DocumentCategoryItem[]> {
        return this.datatask.Post(3727);
    }

    /**
     * Retrieves a document archive item.
     *
     * @datatask 311
     */
    public async getDocArc(docarcKeyno: string | number): Promise<DocArcGet> {
        return (await this.datatask.Post(311, { docarc_keyno: docarcKeyno }))[0];
    }

    /**
     * Updates the filename after an Office document conversion.
     *
     * @datatask 435
     */
    public async updateDocArcFileName(params: DocArcUpdateFileNameParams): Promise<void> {
        await this.datatask.Post(435, params);
    }

    /**
     * Saves document archive metadata.
     *
     * @datatask 618
     */
    public async saveDocArc(docarc: DocArcGet): Promise<void> {
        await this.datatask.Post(618, docarc);
    }

    /**
     * Deletes a document archive item.
     *
     * @datatask 434
     */
    public async deleteDocArc(docarcKeyno: string | number): Promise<void> {
        await this.datatask.Post(434, { docarcKeyno });
    }

    /**
     * Checks if the file already exists in docarc with the same values.
     *
     * @param filename the filename of the file to check if exists.
     * @param checksum_md5 the checksum of the file to check if already exists.
     * @param argtype the argtype of the file to check if already exists.
     * @param argvalue the argvalue of the file to check if already exists.
     * @param context
     * @returns a promise containing an object with exists.
     */
    public async checkFileExists(filename: string, checksum_md5: string, argtype: string, argvalue: string, context?: string): Promise<{ exists: boolean }> {
        const data = await this.datatask.Post(1636, {
            originalFilename: filename || '',
            checksum_md5: checksum_md5 || '',
            argtype: argtype || '',
            argvalue: argvalue || '',
        });

        return { exists: data[0].docarcExists === '1' };
    }
}

export interface DocumentCategoryItem {
    doccategory_keyno: string | number;
    doccategory_name: string;
    orderby: string | number;
}

export interface DocArcItem {
    filename: string;
    contentType?: string;
    latitude?: string | number;
    longitude?: string | number;
    altitude?: string | number;
    heading?: string | number;
    createdTimestamp?: string;
    originalTimestamp?: string;
    size?: string | number;
    argType?: string;
    argValue?: string;
    checksum_md5?: string | number;
    p2ReportdefKeyno?: `${number}`;
}

export interface DocArcGet extends Partial<FileItem> {
    docarc_keyno: string | number;
    cust_no?: string | number;
    project_keyno?: string | number;
    projectprosess_keyno?: string | number;
    docfilename: string;
    doctitle?: string;
    oleapplication_id?: string | number;
    reg_by_portal_user_keyno?: string | number;
    filesize?: string | number;
    doccategory_keyno?: string | number;
}

export interface DocArcUpdateFileNameParams {
    docarcKeyno: string | number;
    docFileName: string;
}

export interface DocArcSaveEmailItemParams {
    heading: string;
    email_body_html: string;
    recipient_email: string;
    datetime_sent: string;
    datetime_received: string;
    sentDateTimeZulu: string;
    sender_email: string;
    sender_name: string;
    email_cc: string;
    email_bcc: string;
    attachments: Attachment[];
    argtype: string;
    argvalue: string;
}
